'use client';

import { useEffect, useState } from 'react';

import { VIDEO_SOURCE_TYPE } from '@/constants/videoSource';
import { useInView } from '@/hooks/useInView';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

import type { MediaFormat } from '@/lib/mediaFormats';

export type BackgroundVideoPoster = {
  sources: ReadonlyArray<{ src: string; width: number }>;
  /** Next-gen `<picture>` sources (AVIF/WebP) when sibling files exist. */
  formats?: ReadonlyArray<MediaFormat>;
  alt: string;
  width?: number;
  height?: number;
  /** Responsive `sizes` for the poster image (default `100vw`). */
  sizes?: string;
};

/** One self-hosted encode (MP4 + optional WebM), optionally gated by `media`. */
export type BackgroundVideoFileVariant = {
  mp4: string;
  webm?: string;
  /** CSS media query — omit on the fallback (widest) encode. */
  media?: string;
};

export type BackgroundVideoSource =
  | { type: typeof VIDEO_SOURCE_TYPE.YOUTUBE; videoId: string }
  | {
      type: typeof VIDEO_SOURCE_TYPE.FILE;
      /** Ordered variants — first matching `media` wins; last should omit `media`. */
      variants: ReadonlyArray<BackgroundVideoFileVariant>;
    };

export type BackgroundVideoProps = {
  source: BackgroundVideoSource;
  poster: BackgroundVideoPoster;
  /** Extra class on the root, e.g. for a section-specific placeholder colour. */
  className?: string;
};

export function scheduleIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: 2000 });
    return () => window.cancelIdleCallback(id);
  }

  const id = window.setTimeout(callback, 1);
  return () => window.clearTimeout(id);
}

export function prefersReducedData(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } })
    .connection;
  return connection?.saveData === true;
}

/** Gate playback until in-view; respect reduced-motion and save-data. */
export function useBackgroundVideoGate() {
  const { ref, inView } = useInView<HTMLDivElement>({ once: true, margin: '200px' });
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldPlay = inView && !prefersReducedMotion && !prefersReducedData();

  return { ref, shouldPlay };
}

/** Defer mounting the player until idle after the gate opens. */
export function useDeferredPlayerMount(shouldPlay: boolean) {
  const [idleReady, setIdleReady] = useState(false);

  useEffect(() => {
    if (!shouldPlay) return;

    const cancelIdle = scheduleIdle(() => setIdleReady(true));

    return cancelIdle;
  }, [shouldPlay]);

  return shouldPlay && idleReady;
}
